import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "@/config/config";
import {
  buyBooze,
  buyNarcs,
  claimBoozeProfit,
  claimNarcsProfit,
  getAmountLimit,
  getBoozeTotalProfit,
  getCityMarketPrice,
  getLaunchBlock,
  getNarcoTotalProfit,
  getNextBoozeTime,
  getNextNarcsTime,
  getUpdateInterval,
  sellBooze,
  sellNarcs,
  sellWrappedBoozeUnits,
  sellWrappedNarcsUnits,
} from "@/helper/contractFunctions/MafiaSmuggleMarket";
import { SmugglingGood } from "@/types/Smuggling/SmugglingGood";
import { SmugglingInfo } from "@/types/SmugglingInfo";

export interface SmugglingState {
  isSellingWrappedBooze: boolean;
  isSellingWrappedNarcs: boolean;
  nextBoozeTime: number;
  nextNarcsTime: number;
  isSmuggling: boolean;
  boozeHoldings: SmugglingGood[];
  boozePrices: number[];
  boozeCountLimit: number;
  isLoadingBoozeHoldings: boolean;
  isLoadingBoozePrices: boolean;
  narcsHoldings: SmugglingGood[];
  narcsPrices: number[];
  narcsCountLimit: number;
  isLoadingNarcsPrices: boolean;
  isLoadingNarcsHoldings: boolean;
  smugglingMarketLaunchBlock: number;
  smugglingMarketUpdateInterval: number;
  smugglingInfo: SmugglingInfo;
  isLoadingSmugglingInfo: boolean;
  isClaimingSmugglingProfit: boolean;
  boozeTotalProfit: number;
  narcoTotalProfit: number;
}

const initialState: SmugglingState = {
  isSellingWrappedBooze: false,
  isSellingWrappedNarcs: false,
  nextBoozeTime: -1,
  nextNarcsTime: -1,
  isSmuggling: false,
  boozeHoldings: [],
  boozePrices: [],
  boozeCountLimit: 0,
  isLoadingBoozeHoldings: false,
  isLoadingBoozePrices: false,
  narcsHoldings: [],
  narcsPrices: [],
  narcsCountLimit: 0,
  isLoadingNarcsPrices: false,
  isLoadingNarcsHoldings: false,
  smugglingMarketLaunchBlock: -1,
  smugglingMarketUpdateInterval: 200,
  smugglingInfo: {
    totalBuyAmount: 0,
    totalSellAmount: 0,
    totalOwnerProfit: 0,
  },
  isLoadingSmugglingInfo: false,
  isClaimingSmugglingProfit: false,
  boozeTotalProfit: 0,
  narcoTotalProfit: 0,
};

export const claimBoozeProfitAction = createAsyncThunk(
  "smuggling/claimBoozeProfitAction",
  ({ account, cityId }: { account: string; cityId: number }) =>
    claimBoozeProfit(account, cityId)
);

export const claimNarcsProfitAction = createAsyncThunk(
  "smuggling/claimNarcsProfitAction",
  ({ account, cityId }: { account: string; cityId: number }) =>
    claimNarcsProfit(account, cityId)
);

export const getBoozeHoldingsAction = createAsyncThunk(
  "smuggling/getBoozeHoldingsAction",
  async (account: string) =>
    (
      await axios.get(
        `${SERVER_URL}/profile/goods/holding/${account}?categoryId=0`
      )
    ).data
);

export const getNarcsHoldingsAction = createAsyncThunk(
  "smuggling/getNarcsHoldingsAction",
  async (account: string) =>
    (
      await axios.get(
        `${SERVER_URL}/profile/goods/holding/${account}?categoryId=1`
      )
    ).data
);

export const getCityMarketPriceAction = createAsyncThunk(
  "smuggling/getCityMarketPriceAction",
  getCityMarketPrice
);

export const getNextBoozeTimeAction = createAsyncThunk(
  "smuggling/getNextBoozeTimeAction",
  getNextBoozeTime
);

export const getNextNarcsTimeAction = createAsyncThunk(
  "smuggling/getNextNarcsTimeAction",
  getNextNarcsTime
);

export const getAmountLimitAction = createAsyncThunk(
  "smuggling/getAmountLimitAction",
  getAmountLimit
);

export const getSmugglingMarketLaunchBlockAction = createAsyncThunk(
  "smuggling/getSmugglingMarketLaunchBlockAction",
  getLaunchBlock
);

export const getSmugglingMarketUpdateIntervalAction = createAsyncThunk(
  "smuggling/getSmugglingMarketUpdateIntervalAction",
  getUpdateInterval
);

export const buyBoozeAction = createAsyncThunk(
  "smuggling/buyBoozeAction",
  ({
    account,
    types,
    message,
    signature,
  }: {
    account: string;
    types: number[];
    message: string;
    signature: string;
  }) => buyBooze(account, types, message, signature)
);

export const sellBoozeAction = createAsyncThunk(
  "smuggling/sellBoozeAction",
  ({
    account,
    itemIds,
    signMsg,
    signature,
  }: {
    account: string;
    itemIds: number[];
    signMsg: string;
    signature: string;
  }) => sellBooze(account, itemIds, signMsg, signature)
);

export const buyNarcsAction = createAsyncThunk(
  "smuggling/buyNarcsAction",
  ({
    account,
    types,
    signMsg,
    signature,
  }: {
    account: string;
    types: number[];
    signMsg: string;
    signature: string;
  }) => buyNarcs(account, types, signMsg, signature)
);

export const sellNarcsAction = createAsyncThunk(
  "smuggling/sellNarcsAction",
  ({
    account,
    itemIds,
    signMsg,
    signature,
  }: {
    account: string;
    itemIds: number[];
    signMsg: string;
    signature: string;
  }) => sellNarcs(account, itemIds, signMsg, signature)
);

export const sellWrappedBoozeUnitsAction = createAsyncThunk("smuggling/sellWrappedBoozeUnitsAction", async ({ account, itemIds, signMsg, signature }: { account: string; itemIds: number[]; signMsg: string; signature: string }) => sellWrappedBoozeUnits(account, itemIds, signMsg, signature));
export const sellWrappedNarcsUnitsAction = createAsyncThunk("smuggling/sellWrappedNarcsUnitsAction", async ({ account, itemIds, signMsg, signature }: { account: string; itemIds: number[]; signMsg: string; signature: string }) => sellWrappedNarcsUnits(account, itemIds, signMsg, signature));
export const getSmugglingInfoAction = createAsyncThunk(
  "smuggling/getSmugglingInfoAction",
  async ({ categoryId, cityId }: { categoryId: number; cityId: number }) =>
    (
      await axios.get(
        `${SERVER_URL}/profile/goods/info?categoryId=${categoryId}&cityId=${cityId}`
      )
    ).data
);

export const getBoozeTotalProfitAction = createAsyncThunk(
  "smuggling/getBoozeTotalProfitAction",
  ({ city }: { city: number }) => getBoozeTotalProfit(city)
);

export const getNarcoTotalProfitAction = createAsyncThunk(
  "smuggling/getNarcoTotalProfitAction",
  ({ city }: { city: number }) => getNarcoTotalProfit(city)
);

const smugglingSlice = createSlice({
  name: "smuggling",
  initialState,
  reducers: {
    finishSmuggle: (state) => {
      state.isSmuggling = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getSmugglingMarketLaunchBlockAction.fulfilled,
        (state, { payload }) => {
          state.smugglingMarketLaunchBlock = payload;
        }
      )
      .addCase(
        getSmugglingMarketUpdateIntervalAction.fulfilled,
        (state, { payload }) => {
          state.smugglingMarketUpdateInterval = payload;
        }
      )
      .addCase(getCityMarketPriceAction.pending, (state) => {
        state.isLoadingBoozePrices = true;
        state.isLoadingNarcsPrices = true;
      })
      .addCase(getCityMarketPriceAction.fulfilled, (state, { payload }) => {
        state.boozePrices = payload[0];
        state.narcsPrices = payload[1];
        state.isLoadingBoozePrices = false;
        state.isLoadingNarcsPrices = false;
      })
      .addCase(getCityMarketPriceAction.rejected, (state) => {
        state.boozePrices = [];
        state.narcsPrices = [];
        state.isLoadingBoozePrices = false;
        state.isLoadingNarcsPrices = false;
      })
      .addCase(getNextBoozeTimeAction.fulfilled, (state, { payload }) => {
        state.nextBoozeTime = payload;
      })
      .addCase(getNextBoozeTimeAction.rejected, (state) => {
        state.nextBoozeTime = -1;
      })
      .addCase(getNextNarcsTimeAction.fulfilled, (state, { payload }) => {
        state.nextNarcsTime = payload;
      })
      .addCase(getNextNarcsTimeAction.rejected, (state) => {
        state.nextNarcsTime = -1;
      })
      .addCase(getAmountLimitAction.fulfilled, (state, { payload }) => {
        state.boozeCountLimit = payload[0];
        state.narcsCountLimit = payload[1];
      })
      .addCase(getAmountLimitAction.rejected, (state, { error }) => {
        state.boozeCountLimit = 0;
        state.narcsCountLimit = 0;
        console.log(error);
      });

    const smuggleActions = [
      buyBoozeAction,
      sellBoozeAction,
      buyNarcsAction,
      sellNarcsAction,
    ] as const;
    smuggleActions.forEach((action) => {
      builder
        .addCase(action.pending, (state) => {
          state.isSmuggling = true;
        })
        .addCase(action.rejected, (state, { error }) => {
          state.isSmuggling = false;
          console.log(error);
        });
    });

    builder
      .addCase(getBoozeHoldingsAction.pending, (state) => {
        state.isLoadingBoozeHoldings = true;
      })
      .addCase(getBoozeHoldingsAction.fulfilled, (state, { payload }) => {
        state.isLoadingBoozeHoldings = false;
        state.boozeHoldings = payload;
      })
      .addCase(getBoozeHoldingsAction.rejected, (state) => {
        state.isLoadingBoozeHoldings = false;
      })
      .addCase(getNarcsHoldingsAction.pending, (state) => {
        state.isLoadingBoozeHoldings = true;
      })
      .addCase(getNarcsHoldingsAction.fulfilled, (state, { payload }) => {
        state.isLoadingBoozeHoldings = false;
        state.narcsHoldings = payload;
      })
      .addCase(getNarcsHoldingsAction.rejected, (state) => {
        state.isLoadingBoozeHoldings = false;
      })
      .addCase(getSmugglingInfoAction.pending, (state) => {
        state.isLoadingSmugglingInfo = true;
      })
      .addCase(getSmugglingInfoAction.fulfilled, (state, { payload }) => {
        state.isLoadingSmugglingInfo = false;
        state.smugglingInfo = payload;
      })
      .addCase(getSmugglingInfoAction.rejected, (state, { error }) => {
        state.isLoadingSmugglingInfo = false;
        console.log(error);
      });

    const claimActions = [
      claimBoozeProfitAction,
      claimNarcsProfitAction,
    ] as const;
    claimActions.forEach((action) => {
      builder
        .addCase(action.pending, (state) => {
          state.isClaimingSmugglingProfit = true;
        })
        .addCase(action.fulfilled, (state) => {
          state.isClaimingSmugglingProfit = false;
        })
        .addCase(action.rejected, (state) => {
          state.isClaimingSmugglingProfit = false;
        });
    });

    builder
      .addCase(sellWrappedBoozeUnitsAction.pending, (state) => { state.isSellingWrappedBooze = true; })
      .addCase(sellWrappedBoozeUnitsAction.fulfilled, (state) => { state.isSellingWrappedBooze = false; })
      .addCase(sellWrappedBoozeUnitsAction.rejected, (state) => { state.isSellingWrappedBooze = false; })
      .addCase(sellWrappedNarcsUnitsAction.pending, (state) => { state.isSellingWrappedNarcs = true; })
      .addCase(sellWrappedNarcsUnitsAction.fulfilled, (state) => { state.isSellingWrappedNarcs = false; })
      .addCase(sellWrappedNarcsUnitsAction.rejected, (state) => { state.isSellingWrappedNarcs = false; })
      .addCase(getBoozeTotalProfitAction.fulfilled, (state, { payload }) => {
        state.boozeTotalProfit = payload;
      })
      .addCase(getNarcoTotalProfitAction.fulfilled, (state, { payload }) => {
        state.narcoTotalProfit = payload;
      });
  },
});

export const { finishSmuggle } = smugglingSlice.actions;
export default smugglingSlice.reducer;
